import FilterSortIcon from '@assets/icons/filter-sort.svg';
import i18n from '@/utils/i18n';
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  Button,
  Checkbox,
  Popover,
  RadioGroup,
  SearchInput,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  INBOUND_SORT_OPTIONS,
  InboundAssigneeOption,
  InboundListFilter,
} from './types';
import {
  type InboundListQueryFilters,
  useOrderInboundsStore,
} from '@/stores/order-inbounds-store';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { SmartOrderInboundHistoryOrderBy } from '@/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useInboundAssigneeOptions } from '@/screens/order-inbounds/hooks/use-inbound-assignee-options';

const POPOVER_WIDTH = 278;
/** Figma caps the scrolling body at 500; the footer stays pinned below it. */
const CONTENT_MAX_HEIGHT = 500;
/** Gap between a section's label and its list -- not on the spacing scale. */
const SECTION_LABEL_GAP = 10;
const CONTROL_SIZE = 16;
const RADIO_DOT_SIZE = 8;
const TRIGGER_SIZE = 40;
const TRIGGER_ICON_SIZE = 20;
const BADGE_SIZE = 16;

/**
 * Tamagui's radio Item frame ships its own hover/press fills, meant for the
 * bare 16px circle it normally renders. Here the Item is the whole row and
 * Figma gives the row no fill of its own, so both states are zeroed out.
 */
const NO_ROW_FILL = { backgroundColor: 'transparent' } as const;

/** The subset of the applied filters this panel owns; search stays with Formik. */
type StagedFilters = Pick<
  InboundListQueryFilters,
  'assignedEmployeeIds' | 'includeUnassigned' | 'sortBy'
>;

const CLEARED_FILTERS: StagedFilters = {
  assignedEmployeeIds: [],
  includeUnassigned: false,
  sortBy: SmartOrderInboundHistoryOrderBy.NewestFirst,
};

/**
 * Sort + "Assigned to" panel for the inbound history list, opened from the
 * filter icon beside the search input. A Popover on web; sprout-kit's Popover
 * swaps itself for a drag-to-dismiss bottom sheet on native.
 *
 * Edits are *staged*: every control writes to local state and nothing reaches
 * the query until Apply. Dismissing any other way (outside press, escape,
 * swipe down) throws the staged edits away, and the next open re-seeds from
 * whatever is currently applied.
 */
export const OrderInboundsOrderListFilterPanel: FC = () => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const queryFilters = useOrderInboundsStore((store) => store.queryFilters);
  const setQueryFilters = useOrderInboundsStore(
    (store) => store.actions.setQueryFilters,
  );
  const activeStatusFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );

  const { error, loading, options } = useInboundAssigneeOptions();

  // Applied assignee IDs are restored from device storage, so one may belong
  // to an employee who has since been removed. Drop those once the option list
  // is known, so the badge count and the query agree. A failed fetch leaves the
  // filter alone rather than wiping a valid selection.
  useEffect(() => {
    if (loading || error) {
      return;
    }
    const known = new Set(options.map((option) => option.employeeUUID));
    const pruned = queryFilters.assignedEmployeeIds.filter((id) =>
      known.has(id),
    );
    if (pruned.length !== queryFilters.assignedEmployeeIds.length) {
      setQueryFilters({ assignedEmployeeIds: pruned });
    }
  }, [
    error,
    loading,
    options,
    queryFilters.assignedEmployeeIds,
    setQueryFilters,
  ]);

  const [staged, setStaged] = useState<StagedFilters>(CLEARED_FILTERS);

  const [assigneeSearch, setAssigneeSearch] = useState('');

  const visibleOptions = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [assigneeSearch, options]);

  const isSearching = assigneeSearch.trim().length > 0;

  // A cutoff is meaningless for orders that are already closed, so the
  // option is unusable on that tab. The applied sort is reset elsewhere (on tab
  // change); this only stops the user picking it while they're there.
  const isCutoffSortDisabled = activeStatusFilter === InboundListFilter.Closed;

  const appliedAssigneeCount =
    queryFilters.assignedEmployeeIds.length +
    (queryFilters.includeUnassigned ? 1 : 0);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        // Seed from what's applied, so a dismissed edit never leaks into the
        // next open.
        setStaged({
          assignedEmployeeIds: queryFilters.assignedEmployeeIds,
          includeUnassigned: queryFilters.includeUnassigned,
          sortBy: queryFilters.sortBy,
        });
      }
      setAssigneeSearch('');
      setIsOpen(open);
    },
    [queryFilters],
  );

  const toggleStagedEmployee = useCallback((employeeUUID: string) => {
    setStaged((current) => ({
      ...current,
      assignedEmployeeIds: current.assignedEmployeeIds.includes(employeeUUID)
        ? current.assignedEmployeeIds.filter((id) => id !== employeeUUID)
        : [...current.assignedEmployeeIds, employeeUUID],
    }));
  }, []);

  const onApply = useCallback(() => {
    setQueryFilters(staged);
    setAssigneeSearch('');
    setIsOpen(false);
  }, [setQueryFilters, staged]);

  // Resets the staged values only -- the user still has to press Apply, and the
  // order search input is left alone.
  const onClearAll = useCallback(() => {
    setStaged(CLEARED_FILTERS);
    setAssigneeSearch('');
  }, []);

  const sortSection = useMemo(
    () => (
      <View style={styles.section}>
        <SectionLabel>{i18n.t('order_inbound.filter_panel.sort')}</SectionLabel>
        <RadioGroup
          onValueChange={(value) =>
            setStaged((current) => ({
              ...current,
              sortBy: value as SmartOrderInboundHistoryOrderBy,
            }))
          }
          value={staged.sortBy}
          width="100%"
        >
          {INBOUND_SORT_OPTIONS.map((option) => {
            const disabled =
              isCutoffSortDisabled &&
              option.value === SmartOrderInboundHistoryOrderBy.ClosestCutoff;
            const selected = staged.sortBy === option.value;

            return (
              <RadioGroup.Item
                alignItems="center"
                backgroundColor="transparent"
                borderRadius={0}
                borderWidth={0}
                cursor={disabled ? 'not-allowed' : 'pointer'}
                disabled={disabled}
                flexDirection="row"
                gap="$8"
                height="auto"
                hoverStyle={NO_ROW_FILL}
                justifyContent="flex-start"
                key={option.value}
                paddingVertical="$8"
                pressStyle={NO_ROW_FILL}
                testID={`inbound-filter-panel-sort-${option.value}`}
                value={option.value}
                width="100%"
              >
                <View
                  style={StyleSheet.flatten([
                    styles.radio,
                    selected && styles.radioSelected,
                    disabled && styles.controlDisabled,
                  ])}
                >
                  <RadioGroup.Indicator
                    backgroundColor="$bgCheckboxActive"
                    height={RADIO_DOT_SIZE}
                    width={RADIO_DOT_SIZE}
                  />
                </View>
                <Typography
                  color={
                    disabled
                      ? theme.sk.color.text.disabled
                      : theme.sk.color.text.primary
                  }
                  flexShrink={1}
                  variant="paragraph-md"
                >
                  {i18n.t(option.labelKey)}
                </Typography>
              </RadioGroup.Item>
            );
          })}
        </RadioGroup>
      </View>
    ),
    [isCutoffSortDisabled, staged.sortBy, styles, theme],
  );

  const assigneeSection = (
    <View style={styles.section}>
      <SectionLabel>
        {i18n.t('order_inbound.filter_panel.assigned_to')}
      </SectionLabel>

      {loading ? (
        <View style={styles.loading} testID="inbound-filter-panel-loading">
          <ActivityIndicator />
        </View>
      ) : (
        <>
          <View
            style={styles.assigneeSearch}
            testID="inbound-filter-panel-assignee-search"
          >
            <SearchInput
              aria-label={i18n.t('order_inbound.filter_panel.search_assignees')}
              clearAriaLabel={i18n.t('clear_search')}
              onChangeText={setAssigneeSearch}
              onClear={() => setAssigneeSearch('')}
              placeholder={i18n.t(
                'order_inbound.filter_panel.search_assignees',
              )}
              size="$md"
              value={assigneeSearch}
            />
          </View>
          {/* "Unassigned" is a status, not a person, so it sits outside the
              searched list and stays put while the user types. */}
          <AssigneeRow
            checked={staged.includeUnassigned}
            label={i18n.t('order_inbound.filter_panel.unassigned')}
            onToggle={() =>
              setStaged((current) => ({
                ...current,
                includeUnassigned: !current.includeUnassigned,
              }))
            }
            testID="inbound-filter-panel-assignee-unassigned"
          />
          {visibleOptions.map((option) => (
            <AssigneeRow
              checked={staged.assignedEmployeeIds.includes(option.employeeUUID)}
              key={option.employeeUUID}
              label={option.label}
              onToggle={() => toggleStagedEmployee(option.employeeUUID)}
              testID={`inbound-filter-panel-assignee-${option.employeeUUID}`}
            />
          ))}
          {isSearching && visibleOptions.length === 0 ? (
            <Typography
              color={theme.sk.color.text.secondary}
              testID="inbound-filter-panel-assignee-empty"
              variant="paragraph-sm"
            >
              {i18n.t('order_inbound.filter_panel.no_assignees_match')}
            </Typography>
          ) : null}
          {error ? (
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {i18n.t('order_inbound.filter_panel.assignees_error')}
            </Typography>
          ) : null}
        </>
      )}
    </View>
  );

  const sections = (
    <View style={styles.scrollContent}>
      {sortSection}
      {assigneeSection}
    </View>
  );

  const content = (
    <View>
      {/* Native renders inside sprout-kit's Sheet, whose frame ignores
          maxHeight and whose drag-to-dismiss gesture fights a plain
          ScrollView; Sheet.ScrollView caps the height and scrolls without
          dismissing the sheet. Web keeps the RN ScrollView capped by
          `styles.scroll` inside the floating popover. */}
      {Platform.OS === 'web' ? (
        <ScrollView style={styles.scroll}>{sections}</ScrollView>
      ) : (
        <Popover.Sheet.ScrollView maxHeight={CONTENT_MAX_HEIGHT}>
          {sections}
        </Popover.Sheet.ScrollView>
      )}

      <View style={styles.footer}>
        <Button
          accessibilityLabel={i18n.t('clear_all')}
          flex={1}
          onPress={onClearAll}
          size="$md"
          testID="inbound-filter-panel-clear-all"
          variant="tertiary"
          width={120}
        >
          {i18n.t('clear_all')}
        </Button>
        <Button
          accessibilityLabel={i18n.t('order_inbound.filter_panel.apply')}
          flex={1}
          onPress={onApply}
          size="$md"
          testID="inbound-filter-panel-apply"
          variant="primary"
          width={120}
        >
          {i18n.t('order_inbound.filter_panel.apply')}
        </Button>
      </View>
    </View>
  );

  return (
    <Popover onOpenChange={onOpenChange} open={isOpen} placement="bottom-end">
      <Popover.Trigger asChild>
        <Pressable
          accessibilityLabel={i18n.t('order_inbound.filter_panel.trigger')}
          accessibilityRole="button"
          style={({ hovered, pressed }) =>
            StyleSheet.flatten([
              styles.trigger,
              (hovered || pressed || isOpen) && styles.triggerActive,
            ])
          }
          testID="inbound-filter-panel-trigger"
        >
          <FilterSortIcon
            height={TRIGGER_ICON_SIZE}
            stroke={theme.sk.color.icon.secondary}
            width={TRIGGER_ICON_SIZE}
          />
          {appliedAssigneeCount > 0 ? (
            <View
              style={styles.badge}
              testID="inbound-filter-panel-trigger-badge"
            >
              <Typography
                color={theme.sk.color.text.ondark}
                variant="paragraph-sm-600"
              >
                {appliedAssigneeCount}
              </Typography>
            </View>
          ) : null}
        </Pressable>
      </Popover.Trigger>

      <Popover.Content
        borderColor="$borderPrimary"
        borderWidth={1}
        width={Platform.OS === 'web' ? POPOVER_WIDTH : 'auto'}
      >
        {content}
      </Popover.Content>
    </Popover>
  );
};

const SectionLabel: FC<{ children: string }> = ({ children }) => {
  const [, theme] = useStyles();

  return (
    <Typography color={theme.sk.color.text.tertiary} variant="paragraph-sm-600">
      {children}
    </Typography>
  );
};

interface AssigneeRowProps {
  checked: boolean;
  label: string;
  onToggle: () => void;
  testID: string;
}

const AssigneeRow: FC<AssigneeRowProps> = ({
  checked,
  label,
  onToggle,
  testID,
}) => {
  const [styles, theme] = useStyles();

  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      onPress={onToggle}
      style={styles.row}
      testID={testID}
    >
      {/* The row owns the press; the checkbox is purely the indicator, so a
          tap can't toggle twice. */}
      <View pointerEvents="none">
        <Checkbox checked={checked} size="$sm" />
      </View>
      <Typography
        color={theme.sk.color.text.primary}
        flexShrink={1}
        variant="paragraph-md"
      >
        {label}
      </Typography>
    </Pressable>
  );
};

export type { InboundAssigneeOption };

const useStyles = createStyles((theme) => {
  // Figma insets the panel by 16. On native the Popover renders itself inside
  // sprout-kit's Sheet, whose frame already contributes 12 of that.
  const horizontalPadding =
    Platform.OS === 'web'
      ? theme.sk.spacing.spaceMedium
      : theme.sk.spacing.spaceXsmall;

  return {
    assigneeSearch: {
      marginBottom: theme.sk.spacing.spaceXsmall,
    },
    badge: {
      alignItems: 'center',
      backgroundColor: theme.sk.color.bg.button.primary.default,
      borderRadius: theme.sk.radii.radiusRound,
      height: BADGE_SIZE,
      justifyContent: 'center',
      minWidth: BADGE_SIZE,
      paddingHorizontal: theme.sk.spacing.spaceXsmall,
      position: 'absolute',
      right: -4,
      top: -4,
    },
    controlDisabled: {
      opacity: 0.5,
    },
    footer: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.sk.spacing.spaceSmall,
      paddingHorizontal: horizontalPadding,
      paddingVertical: theme.sk.spacing.spaceMedium,
    },
    loading: {
      alignItems: 'center',
      paddingVertical: theme.sk.spacing.spaceMedium,
    },
    radio: {
      alignItems: 'center',
      backgroundColor: theme.sk.color.bg.primary.default,
      borderColor: theme.sk.color.border.checkbox.default,
      borderRadius: theme.sk.radii.radiusRound,
      borderWidth: 1,
      height: CONTROL_SIZE,
      justifyContent: 'center',
      width: CONTROL_SIZE,
    },
    radioSelected: {
      borderColor: theme.sk.color.bg.checkbox.active,
    },
    row: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.sk.spacing.spaceSmall,
      paddingVertical: theme.sk.spacing.spaceSmall,
    },
    scroll: {
      flexGrow: 0,
      maxHeight: CONTENT_MAX_HEIGHT,
    },
    scrollContent: {
      gap: theme.sk.spacing.spaceLarge,
      paddingHorizontal: horizontalPadding,
      paddingVertical: theme.sk.spacing.spaceMedium,
    },
    section: {
      gap: SECTION_LABEL_GAP,
    },
    trigger: {
      alignItems: 'center',
      backgroundColor: theme.sk.color.bg.button.secondary.default,
      borderColor: theme.sk.color.border.input.default,
      borderRadius: theme.sk.radii.radiusMedium,
      borderWidth: 1,
      height: TRIGGER_SIZE,
      justifyContent: 'center',
      position: 'relative',
      width: TRIGGER_SIZE,
    },
    triggerActive: {
      backgroundColor: theme.sk.color.bg.button.secondary.hover,
    },
  };
});
